(function () {
  'use strict';

  if (window.KohWishlist) return;

  const STORAGE_KEY = 'kohsnkrs_wishlist_v1';
  const CHANGE_EVENT = 'koh:wishlist:change';
  const BUTTON_SELECTOR = '[data-koh-wishlist-toggle]';
  const UI_SELECTOR = `${BUTTON_SELECTOR}, [data-koh-wishlist-header-link], [data-koh-wishlist-count]`;
  let memoryItems = [];
  let storageUsable = true;
  let toastTimer = 0;
  let syncQueued = false;

  const normalizeItem = (rawItem) => {
    if (!rawItem || typeof rawItem !== 'object') return null;
    const id = String(rawItem.id || '').trim();
    const handle = String(rawItem.handle || '').trim();
    if (!id || !handle || id.length > 80 || handle.length > 255) return null;

    const item = {
      id,
      handle,
      url: typeof rawItem.url === 'string' ? rawItem.url : '',
    };
    const addedAt = Number(rawItem.addedAt);
    if (Number.isFinite(addedAt) && addedAt > 0) item.addedAt = addedAt;
    return item;
  };

  const sanitize = (value) => {
    if (!Array.isArray(value)) return [];
    const unique = new Map();
    value.forEach((rawItem) => {
      const item = normalizeItem(rawItem);
      if (item && !unique.has(item.id)) unique.set(item.id, item);
    });
    return Array.from(unique.values());
  };

  const read = () => {
    if (!storageUsable) return memoryItems.slice();
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) memoryItems = [];
      else {
        try {
          memoryItems = sanitize(JSON.parse(raw));
        } catch (parseError) {
          // Keep the storefront usable without throwing or deleting the damaged value automatically.
          memoryItems = [];
        }
      }
    } catch (error) {
      storageUsable = false;
      memoryItems = sanitize(memoryItems);
    }
    return memoryItems.slice();
  };

  const write = (items) => {
    memoryItems = sanitize(items);
    if (storageUsable) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryItems));
      } catch (error) {
        storageUsable = false;
      }
    }
    return memoryItems.slice();
  };

  const contains = (id) => read().some((item) => item.id === String(id));

  const announce = (message, duration) => {
    if (!message) return;
    let toast = document.querySelector('[data-koh-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'koh-wishlist-toast';
      toast.dataset.kohWishlistToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toast);
    }
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.dataset.kohWishlistVisible = 'true';
    toastTimer = window.setTimeout(() => {
      toast.dataset.kohWishlistVisible = 'false';
    }, Number(duration) || 2200);
  };

  const updateButton = (button, items) => {
    const id = button.dataset.kohWishlistProductId;
    if (!id) return;
    const active = items.some((item) => item.id === String(id));
    const title = button.dataset.kohWishlistProductTitle || '';
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'}${title ? ` ${title}` : ''} ${active ? 'from' : 'to'} wishlist`);
  };

  const sync = (root) => {
    const items = read();
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(BUTTON_SELECTOR).forEach((button) => updateButton(button, items));
    if (scope.matches && scope.matches(BUTTON_SELECTOR)) updateButton(scope, items);

    document.querySelectorAll('[data-koh-wishlist-count]').forEach((badge) => {
      const countText = items.length > 99 ? '99+' : String(items.length);
      if (badge.textContent !== countText) badge.textContent = countText;
      badge.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-koh-wishlist-header-link]').forEach((link) => {
      link.setAttribute('aria-label', items.length ? `Wishlist, ${items.length} items` : 'Wishlist');
    });
    return items;
  };

  const emitChange = (detail) => {
    const items = sync(document);
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      bubbles: true,
      detail: Object.assign({ items, count: items.length }, detail || {}),
    }));
  };

  const toggle = (rawItem, options) => {
    const item = normalizeItem(rawItem);
    if (!item) return false;
    const items = read();
    const index = items.findIndex((entry) => entry.id === item.id);
    const added = index === -1;
    if (added) items.push(Object.assign({}, item, { addedAt: item.addedAt || Date.now() }));
    else items.splice(index, 1);
    write(items);
    emitChange({ item, added, source: options && options.source });
    if (!options || options.silent !== true) announce(added ? 'Added to wishlist' : 'Removed from wishlist');
    return added;
  };

  const remove = (id, options) => {
    const items = read();
    const index = items.findIndex((item) => item.id === String(id));
    if (index === -1) return null;
    const removedItem = items[index];
    items.splice(index, 1);
    write(items);
    emitChange({ id: String(id), item: removedItem, index, added: false, source: options && options.source });
    if (!options || options.silent !== true) announce('Removed from wishlist');
    return { item: removedItem, index };
  };

  const restore = (rawItem, index, options) => {
    const item = normalizeItem(rawItem);
    if (!item) return false;
    const items = read();
    if (items.some((entry) => entry.id === item.id)) return false;
    const insertionIndex = Math.max(0, Math.min(Number.isFinite(Number(index)) ? Number(index) : items.length, items.length));
    items.splice(insertionIndex, 0, item);
    write(items);
    emitChange({ item, index: insertionIndex, added: true, restored: true, source: options && options.source });
    if (!options || options.silent !== true) announce('Restored to wishlist');
    return true;
  };

  const clear = (options) => {
    const previousItems = read();
    if (previousItems.length === 0) return [];
    write([]);
    emitChange({ cleared: true, previousItems, source: options && options.source });
    if (!options || options.silent !== true) announce('Wishlist cleared');
    return previousItems;
  };

  window.KohWishlist = Object.freeze({
    storageKey: STORAGE_KEY,
    eventName: CHANGE_EVENT,
    getItems: read,
    contains,
    toggle,
    remove,
    restore,
    clear,
    sync,
    announce,
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target && event.target.parentElement;
    const button = target && target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    toggle({
      id: button.dataset.kohWishlistProductId,
      handle: button.dataset.kohWishlistProductHandle,
      url: button.dataset.kohWishlistProductUrl,
    }, { source: 'toggle-button' });
  }, true);

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) emitChange({ external: true, source: 'storage' });
  });

  window.addEventListener('pageshow', () => sync(document));
  document.addEventListener('shopify:section:load', (event) => sync(event.target));
  document.addEventListener('shopify:block:select', (event) => sync(event.target));

  const observer = new MutationObserver((records) => {
    const hasNewWishlistUi = records.some((record) => Array.from(record.addedNodes).some((node) => (
      node instanceof Element && (node.matches(UI_SELECTOR) || node.querySelector(UI_SELECTOR))
    )));
    if (!hasNewWishlistUi || syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      sync(document);
    });
  });

  const start = () => {
    sync(document);
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
